/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

/* eslint-disable no-duplicate-imports */
import { IncrementalQuantity, ITelemetryContent, Module, Node } from '@msdyn365-commerce-modules/utilities';
import * as React from 'react';

import {
    IBuyboxAddToCartViewProps,
    IBuyboxAddToOrderTemplateViewProps,
    IBuyboxAddToWishlistViewProps,
    IBuyboxCallbacks,
    IBuyboxKeyInPriceViewProps,
    IBuyboxProductConfigureDropdownViewProps,
    IBuyboxProductConfigureViewProps,
    IBuyboxProductQuantityViewProps,
    IBuyboxShopSimilarLookViewProps,
    IBuyboxState
} from '@msdyn365-commerce-modules/buybox';
import { IBuyboxViewProps } from './cepheo-buybox';
import { IBuyboxFindInStoreViewProps } from './components/cepheo-buybox-find-in-store';
import {
    ICepheoBuyboxProps as IBuyboxExtentionProps,
    ICepheoBuyboxResources as IBuyboxExtentionResources
} from './cepheo-buybox.props.autogenerated';
import { ICepheoBuyboxData } from './cepheo-buybox.data';
import { IGetProductPackagingPropsResponse, IGetProductCustomerPricesResponse } from '../../dataActions/DataServiceEntities.g';

const BuyboxView: React.FC<IBuyboxViewProps & IBuyboxExtentionProps<ICepheoBuyboxData>> = props => {
    const {
        ModuleProps,
        MediaGalleryContainerProps,
        ProductInfoContainerProps,
        addToCart,
        addToOrderTemplate,
        addToWishlist,
        telemetryContent,
        configure,
        description,
        findInStore,
        quantity,
        price,
        title,
        rating,
        callbacks,
        state,
        resources,
        inventoryLabel,
        shopSimilarLook,
        keyInPrice,
        quantityLimitsMessages,
        max,
        shopSimilarDescription,
        unitOfMeasure,
        packagingProps,
        customerPrices
    } = props;

    return (
        <Module {...ModuleProps}>
            <Node {...MediaGalleryContainerProps}>{props.mediaGallery}</Node>
            <Node {...ProductInfoContainerProps}>
                {title}
                {price}
                {unitOfMeasure}
                {props.bulkPurchaseLink}
                {description}
                {_renderTextBlock(props.slots.textBlocks)}
                {rating}
                {configure && _renderConfigure(configure)}
                {keyInPrice && _renderKeyInPrice(keyInPrice)}
                {quantity && _renderQuantity(quantity, callbacks, props, state, resources, quantityLimitsMessages, max, telemetryContent)}
                {_renderPackagingProps(packagingProps)}
                {_renderCustomerPrices(customerPrices)}
                {inventoryLabel}
                {_renderCartAndActions(addToCart, addToOrderTemplate, addToWishlist)}
                {findInStore && _renderFindInStore(findInStore)}
                {_renderSocialShare(props.slots && props.slots.socialShare)}
                {shopSimilarLook && _renderShopSimilarItem(shopSimilarLook)}
                {shopSimilarDescription && _renderShopSimilarItem(shopSimilarDescription)}
            </Node>
        </Module>
    );
};

const _renderPackagingProps = (packagingProps: IGetProductPackagingPropsResponse | undefined): JSX.Element | undefined => {
    if (packagingProps === undefined || packagingProps.Error || !packagingProps.PropsFound) {
        return <div className='ms-buybox__product-unit-of-measure'><span>Ingen pakke mengde fundet</span></div>;
    }

    return (
        <div className='ms-buybox__product-unit-of-measure'>Antal per palle: {packagingProps.PackagingQty} {packagingProps.InventUnitId}</div>
    );
};

const _renderCustomerPrices = (customerPrices: IGetProductCustomerPricesResponse | undefined): JSX.Element | undefined => {
    if (customerPrices === undefined || customerPrices.Error || !customerPrices.PriceFound || !customerPrices.ProductCustomerTradeAgreements) {
        return <div className='ms-buybox__product-unit-of-measure'><span>Ingen pris fundet - kontakt venligst kundeservice</span></div>;
    }

    // Sort the product customer trade agreements by FromQty
    const sortedTradeAgreements = [...customerPrices.ProductCustomerTradeAgreements].sort(
        (a, b) => a.FromQty - b.FromQty
    );

    const customerPriceTable = {
        table: {
          borderCollapse: 'collapse' as 'collapse'
        },
        th: {
            border: '1px solid #dddddd',
            textAlign: 'left' as 'left',
            padding: '8px',
            fontWeight: 'bold' as 'bold'
          },
          td: {
            border: '1px solid #dddddd',
            textAlign: 'left' as 'left',
            padding: '8px'
          }
        };

    return (
        <div className='ms-buybox__product-unit-of-measure'>
            <table style={customerPriceTable.table}>
                <thead>
                    <tr>
                        <th style={customerPriceTable.th}>Fra</th>
                        <th style={customerPriceTable.th}>Måleenhed</th>
                        <th style={customerPriceTable.th}>Pris</th>
                    </tr>
                </thead>
                <tbody>
                    {sortedTradeAgreements.map((productCustomerTradeAgreement, index) => (
                        <tr key={index}>
                            <td style={customerPriceTable.td}>{productCustomerTradeAgreement.FromQty}</td>
                            <td style={customerPriceTable.td}>{productCustomerTradeAgreement.UnitId}</td>
                            <td style={customerPriceTable.td}>{productCustomerTradeAgreement.SalesPrice.toFixed(2)}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
};

const _renderTextBlock = (textBlocks: React.ReactNode[]): JSX.Element | undefined => {
    if (!textBlocks || textBlocks.length === 0) {
        return undefined;
    }

    return <>{textBlocks[0]}</>;
};

const _renderCartAndActions = (
    addToCart?: IBuyboxAddToCartViewProps,
    addToOrderTemplate?: IBuyboxAddToOrderTemplateViewProps,
    addToWishlist?: IBuyboxAddToWishlistViewProps
): JSX.Element | undefined => {
    if (!addToCart && !addToWishlist) {
        return undefined;
    }

    return (
        <div className='product-add-to-cart'>
            {addToCart && addToCart.errorBlock}
            {addToWishlist && addToWishlist.errorBlock}
            <div className='buttons'>
                {addToCart && addToCart.button}
                {addToWishlist && addToWishlist.button}
            </div>
        </div>
    );
};

const _renderConfigure = (configure: IBuyboxProductConfigureViewProps): JSX.Element => {
    const { ContainerProps, dropdowns } = configure;

    return <Node {...ContainerProps}>{dropdowns.map(_renderConfigureDropdown)}</Node>;
};

const _renderSocialShare = (socialShare: React.ReactNode[]): JSX.Element | undefined => {
    if (!socialShare || socialShare.length === 0) {
        return undefined;
    }

    return <>{socialShare[0]}</>;
};

const _renderConfigureDropdown = (dropdown: IBuyboxProductConfigureDropdownViewProps): JSX.Element => {
    const { ContainerProps, LabelContainerProps, heading, errors, select } = dropdown;

    return (
        <Node {...ContainerProps}>
            <Node {...LabelContainerProps}>
                {heading}
                {errors}
            </Node>
            {select}
        </Node>
    );
};

const _renderFindInStore = (findInStore: IBuyboxFindInStoreViewProps): JSX.Element => {
    const { ContainerProps, storeSelector, heading, description, errors, button, modal, productPickupOptionList } = findInStore;

    return (
        <Node {...ContainerProps}>
            {storeSelector}
            {heading}
            {productPickupOptionList}
            {description}
            {errors}
            {button}
            {modal}
        </Node>
    );
};

const onChangeHandler = (callbacks: IBuyboxCallbacks) => (newValue: number): boolean => {
    if (callbacks.updateQuantity) {
        return callbacks.updateQuantity(newValue);
    }
    return true;
};

const _renderQuantity = (
    quantityComponent: IBuyboxProductQuantityViewProps,
    callbacks: IBuyboxCallbacks,
    props: IBuyboxExtentionProps<ICepheoBuyboxData>,
    state: IBuyboxState,
    extentionResources: IBuyboxExtentionResources,
    quantityLimitsMessages: React.ReactNode,
    max: number | undefined,
    telemetryContent?: ITelemetryContent
): JSX.Element => {
    const { ContainerProps, LabelContainerProps, heading, errors } = quantityComponent;

    const { resources } = props;

    const { quantity } = state;

    return (
        <Node {...ContainerProps}>
            <Node {...LabelContainerProps}>
                {heading}
                {errors}
            </Node>

            <IncrementalQuantity
                id='ms-buybox__product-quantity-input'
                max={max}
                currentCount={quantity}
                onChange={onChangeHandler(callbacks)}
                inputQuantityAriaLabel={resources.inputQuantityAriaLabel}
                decrementButtonAriaLabel={extentionResources.decrementButtonAriaLabel}
                incrementButtonAriaLabel={extentionResources.incrementButtonAriaLabel}
                minQuantityText={extentionResources.minQuantityText}
                maxQuantityText={extentionResources.maxQuantityText}
                telemetryContent={telemetryContent}
            />
            {quantityLimitsMessages}
        </Node>
    );
};

const _renderKeyInPrice = (keyInPrice: IBuyboxKeyInPriceViewProps): JSX.Element => {
    const { ContainerProps, LabelContainerProps, heading, input } = keyInPrice;

    return (
        <Node {...ContainerProps}>
            <Node {...LabelContainerProps}>{heading}</Node>
            {input}
        </Node>
    );
};

const _renderShopSimilarItem = (shopSimilarItem: IBuyboxShopSimilarLookViewProps): JSX.Element => {
    const { ContainerProps, errors, input } = shopSimilarItem;

    return (
        <Node {...ContainerProps}>
            {errors}
            {input}
        </Node>
    );
};
export default BuyboxView;
